import { getCollectionConfig } from '../constants/collections';
import { getEntitledSections, getSectionConfig, type SearchSection, UMBRELLA_SECTIONS } from '../constants/sections';
import type { Filter } from './filters';

// Umbrella type key (`orders`/`bills`) → its collection, so an umbrella token expands
// to every entitled section of that collection instead of matching a section key that
// never exists (the four order sections are keyed `sales-orders` etc, never `orders`).
const UMBRELLA_COLLECTION_BY_KEY = new Map(UMBRELLA_SECTIONS.map((section) => [section.key, section.collection]));

/**
 * Pure composition of the ⌘K palette's two axes into Typesense legs.
 *
 *   SCOPE (depth)   ─▶ which ONE section renders (`resolveQuerySections`)
 *   type filter     ─▶ which sections' legs are issued (breadth, OR ACROSS LEGS)
 *   lifecycle/status─▶ within-leg `filter_by` clauses (AND inside each leg)
 *
 * TYPE = LEG SELECTION, never a merged `filter_by`. Each section keeps its own leg
 * pinned to its discriminator, guaranteeing every type a slot — so `so:` + `po:`
 * issue two `orders` legs (SALES_ORDER, PURCHASE_ORDER), NOT one merged
 * `orderType:=[SALES_ORDER,PURCHASE_ORDER]` leg. Cross-collection type chips are
 * naturally separate legs; the OR is realised across legs in both cases.
 */

/** Which sections' legs to issue. Precedence: scope > typeFacet > typed type pills. */
export function resolveQuerySections(
  entitledSections: readonly SearchSection[],
  scope: string | null,
  filters: readonly Filter[],
  typeFacet: string | null = null
): SearchSection[] {
  if (scope !== null) {
    const inEntitled = entitledSections.find((section) => section.key === scope);
    if (inEntitled) return [inEntitled];
    // A scope is only set to a section the palette can render; fall back to the
    // registry so an umbrella/derived scope still resolves to a single leg.
    const config = getSectionConfig(scope);
    return config ? [config] : [];
  }

  const typeKeys =
    typeFacet !== null
      ? [typeFacet]
      : filters.filter((f): f is Extract<Filter, { dim: 'type' }> => f.dim === 'type').map((f) => f.section);
  if (typeKeys.length === 0) return [...entitledSections];
  // A facetable type key selects its own section; an umbrella key (`orders`/`bills`)
  // selects every entitled section of its collection — so `order:` fans out to all four
  // order sections and `bill:` to both bill sections.
  const wantedSectionKeys = new Set(typeKeys.filter((key) => !UMBRELLA_COLLECTION_BY_KEY.has(key)));
  const wantedCollections = new Set(typeKeys.map((key) => UMBRELLA_COLLECTION_BY_KEY.get(key)).filter(Boolean));
  return entitledSections.filter(
    (section) => wantedSectionKeys.has(section.key) || wantedCollections.has(section.collection)
  );
}

/**
 * The `filter_by` string for one section leg: discriminator AND lifecycle AND
 * status, in that order. Undefined when no clause applies.
 *   - discriminator : the section's pinned `orderType`/`billType`, if any.
 *   - lifecycle     : union of the `is:open`/`is:closed` status sets for THIS
 *                     section (both filters ⇒ open set then closed set).
 *   - status        : the scoped status sub-facet — only when scoped, the section
 *                     has a status field, and the value is a live bucket (the
 *                     status-injection guard; `is:open`/`is:closed` bypass it).
 */
export function buildLegFilterBy(params: {
  section: SearchSection;
  filters: readonly Filter[];
  scoped: boolean;
  liveStatusValues: readonly string[];
  hasStatus: boolean;
}): string | undefined {
  const { section, filters, scoped, liveStatusValues, hasStatus } = params;
  const clauses: string[] = [];

  const discriminator = section.discriminator
    ? `${section.discriminator.field}:=${section.discriminator.value}`
    : undefined;
  if (discriminator) clauses.push(discriminator);

  const lifecycleClause = buildLifecycleClause(section, filters);
  if (lifecycleClause) clauses.push(lifecycleClause);

  const statusFilter = filters.find((f): f is Extract<Filter, { dim: 'status' }> => f.dim === 'status');
  if (scoped && hasStatus && statusFilter && liveStatusValues.includes(statusFilter.value)) {
    clauses.push(`status:=${statusFilter.value}`);
  }

  return clauses.length > 0 ? clauses.join(' && ') : undefined;
}

/** Union the `is:open`/`is:closed` status sets for this section into one clause. */
function buildLifecycleClause(section: SearchSection, filters: readonly Filter[]): string | undefined {
  const lifecycle = filters.filter((f): f is Extract<Filter, { dim: 'lifecycle' }> => f.dim === 'lifecycle');
  if (lifecycle.length === 0 || !section.statusOpenState) return undefined;

  const wantOpen = lifecycle.some((f) => f.open);
  const wantClosed = lifecycle.some((f) => !f.open);
  // Open before closed keeps the clause deterministic when both are applied.
  const set = [
    ...(wantOpen ? section.statusOpenState.open : []),
    ...(wantClosed ? section.statusOpenState.closed : []),
  ];
  return set.length > 0 ? `status:=[${set.join(',')}]` : undefined;
}

/** Entitled, render-capable sections for the all-view candidate set. */
export function entitledRenderableSections(collections: readonly string[]): SearchSection[] {
  return getEntitledSections(collections).filter((section) => getCollectionConfig(section.collection));
}
